<template>
    <div>
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
        <div class="pa-2" v-if="!loading&&player">
            <v-layout row wrap>
                <v-flex xs2 class="pt-3"><strong>{{times[frameIndex]}}</strong></v-flex>
                <v-flex xs9>
                    <v-slider
                            v-model="frameIndex"
                            :max="frames.length-1"
                            @change="refreshData"
                            :min="0"
                    ></v-slider>
                </v-flex>
                <v-flex xs-1 class="pa-3">
                    <v-icon v-if="!interval" class="cursor-as-pointer" @click="play()">fa-play</v-icon>
                    <v-icon v-if="interval" class="cursor-as-pointer" @click="pause()">fa-pause</v-icon>
                </v-flex>
            </v-layout>
        </div>
        <div v-if="!loading&&heatmapData&&renderMode==='idw'"
             :style="{ position: 'relative', width: width + 'px', height: height + 'px' }">
            <img v-if="imageUrl" :src="imageUrl" :width="width" :height="height"
                 style="position: absolute; top: 0; left: 0; display: block;" />
            <canvas ref="heatCanvas" :width="width" :height="height"
                    style="position: absolute; top: 0; left: 0; display: block;" />
            <template v-if="showLabels">
                <span
                    v-for="(pt, i) in heatmapData"
                    :key="'lbl-'+i"
                    :style="{
                        position: 'absolute',
                        left: pt.x + 'px',
                        top: pt.y + 'px',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#fff',
                        textShadow: '0 0 3px #000, 0 0 3px #000',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap'
                    }"
                >{{ pt.value !== null && pt.value !== undefined ? (Math.round(pt.value * 10) / 10) : '' }}</span>
            </template>
        </div>
        <heatmapjs-vue v-if="!loading&&heatmapData&&renderMode!=='idw'" v-bind:style="{ height: height + 'px', width: width + 'px', backgroundImage:imageUrl ? 'url('+imageUrl+')': 'none'}"
                       class="heatmapjs-container" :options="options"
                       :max="max"
                       :min="min" :data="heatmapData"></heatmapjs-vue>
    </div>
</template>

<script>
    import {callAPI} from '@/helper'
    import LoadingProgressBar from './../LoadingProgressBar'
    export default {
        props: {
            dateRange: {type: Object, default: null},
            height: {type: Number, default: 500},
            width: {type: Number, default: 500},
            min: {type: Number, default: 0},
            max: {type: Number, default: 100},
            imageUrl:{type: String, default: null},
            points:{type: Array, default: ()=>{return[]}},
            agg:{type: String, default:"D"},
            aggOpp:{type: String, default:"mean"},
            onOnly:{type: Boolean, default:false},
            player:{type: Boolean, default:false},
            renderMode:{type: String, default:'heatmapjs'},
            boundary:{type: [String, Array], default: null},
            showLabels:{type: Boolean, default: false},
            options: {
                type: Object, default: () => {
                    return {
                        radius: 150,
                        blur: 1,
                        maxOpacity: 0.8,
                    }
                }
            },
        },
        components: {LoadingProgressBar},
        computed: {},
        name: 'HeatmapWidget',
        watch: {
            dateRange () {
                this.refresh()
            },
            heatmapData (val) {
                if (this.renderMode === 'idw' && val) {
                    this.$nextTick(() => this.drawHeatmap())
                }
            },
        },
        data: function () {
            return {
                loading: true,
                heatmapData:null,
                timeout:null,
                frames:[],
                frameIndex:0,
                interval:null,
                times:[]
            }
        },
        mounted(){
            this.refresh()
        },
        beforeDestroy: function(){
            clearInterval(this.interval)
        },
        methods: {
            debounce(callback, wait) {
                clearTimeout(this.timeout);
                this.timeout= setTimeout(() => callback(), wait);
            },
            refresh(){
                this.debounce(this.getData,500);
            },
            refreshData() {
                this.heatmapData=this.frames[this.frameIndex]
            },
            incPlay(){
                if(this.frameIndex<this.frames.length-1){
                    this.frameIndex+=1;
                    this.refreshData()
                } else {
                    clearInterval(this.interval)
                    this.interval=null
                }
            },
            play(){
                let me=this
                me.interval=setInterval(function() { me.incPlay() }, 300);
            },
            pause(){
                clearInterval(this.interval)
                this.interval=null
            },
            getData(){
                let me=this
                me.loading=true
                let pipeline=me.onOnly ? [{"type": "filter","filter_op": "eq", "filter_by": "* - on","filter_value": 1,},{type: "agg", agg: me.agg, agg_op: me.aggOpp}] : [{type: "agg", agg: me.agg, agg_op: me.aggOpp}]
                let promises=me.points.map((point)=>{return callAPI('data3/',{
                    date_range:[this.dateRange.start,this.dateRange.end],
                    filters: {
                        equipment_name: point.equipment_name,
                        sensor_name: point.name,
                        facility_name:point.facility_name ? point.facility_name : me.$store.state.pointsListStore.pointsList.facility_name
                    },
                    pipeline:pipeline
                })})
                Promise.all(promises).then(function(results) {
                    if(me.player){
                        me.times=results[0].data["timestamp"]
                        clearInterval(me.interval)
                        me.interval=null
                        let frames=[]
                        for (let findex in me.times){
                            frames.push(me.points.map((point,index)=>{
                                return {
                                    x:point.x,
                                    y:point.y,
                                    value:results[index].data[point.equipment_name+" - "+point.name] ? results[index].data[point.equipment_name+" - "+point.name][findex] : null
                                }
                            }))
                        }
                        me.frames=frames
                        me.frameIndex=0
                        me.refreshData()
                    } else {
                        me.heatmapData=me.points.map((point,index)=>{
                            return {
                                x:point.x,
                                y:point.y,
                                value:results[index].data[point.equipment_name+" - "+point.name] ? results[index].data[point.equipment_name+" - "+point.name].reduce((acc, curr) => curr!==null ? curr : acc) : null
                            }
                        })
                    }

                    me.loading=false
                })
            },
            drawHeatmap () {
                if (!this.heatmapData || !this.$refs.heatCanvas) return
                const canvas = this.$refs.heatCanvas
                const ctx = canvas.getContext('2d')
                ctx.clearRect(0, 0, this.width, this.height)
                const hasBoundary = this.boundary && (Array.isArray(this.boundary) ? this.boundary.length > 2 : this.boundary.trim().length > 0)
                if (hasBoundary) {
                    ctx.save()
                    let clipPath
                    if (typeof this.boundary === 'string') {
                        clipPath = new Path2D(this.boundary)
                    } else {
                        clipPath = new Path2D()
                        clipPath.moveTo(this.boundary[0].x, this.boundary[0].y)
                        for (let i = 1; i < this.boundary.length; i++) {
                            clipPath.lineTo(this.boundary[i].x, this.boundary[i].y)
                        }
                        clipPath.closePath()
                    }
                    ctx.clip(clipPath)
                }
                const scale = 4
                const offW = Math.ceil(this.width / scale)
                const offH = Math.ceil(this.height / scale)
                const offscreen = document.createElement('canvas')
                offscreen.width = offW
                offscreen.height = offH
                const offCtx = offscreen.getContext('2d')
                const imageData = offCtx.createImageData(offW, offH)
                const pts = this.heatmapData.filter(p => p.value !== null && p.value !== undefined)
                if (pts.length === 0) return
                for (let py = 0; py < offH; py++) {
                    for (let px = 0; px < offW; px++) {
                        const rx = px * scale
                        const ry = py * scale
                        let wSum = 0, vSum = 0
                        for (const pt of pts) {
                            const dx = rx - pt.x
                            const dy = ry - pt.y
                            const d2 = dx * dx + dy * dy
                            if (d2 < 1) { vSum = pt.value; wSum = 1; break }
                            const wt = 1 / d2
                            vSum += wt * pt.value
                            wSum += wt
                        }
                        const interpolated = wSum > 0 ? vSum / wSum : this.min
                        const [r, g, b, a] = this.valueToColor(interpolated)
                        const idx = (py * offW + px) * 4
                        imageData.data[idx] = r
                        imageData.data[idx + 1] = g
                        imageData.data[idx + 2] = b
                        imageData.data[idx + 3] = a
                    }
                }
                offCtx.putImageData(imageData, 0, 0)
                ctx.imageSmoothingEnabled = true
                ctx.imageSmoothingQuality = 'high'
                ctx.drawImage(offscreen, 0, 0, this.width, this.height)
                if (hasBoundary) {
                    ctx.restore()
                }
            },
            valueToColor (value) {
                const t = Math.max(0, Math.min(1, (value - this.min) / (this.max - this.min)))
                const stops = [[0, 0, 255], [0, 255, 255], [0, 255, 0], [255, 255, 0], [255, 0, 0]]
                const pos = t * (stops.length - 1)
                const lo = Math.floor(pos)
                const hi = Math.min(Math.ceil(pos), stops.length - 1)
                const f = pos - lo
                const c1 = stops[lo]
                const c2 = stops[hi]
                return [
                    Math.round(c1[0] + f * (c2[0] - c1[0])),
                    Math.round(c1[1] + f * (c2[1] - c1[1])),
                    Math.round(c1[2] + f * (c2[2] - c1[2])),
                    180
                ]
            }
        },

    }

</script>

<style scoped>

</style>
